import React from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Close, Download } from "@mui/icons-material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import { RightPanel } from "../../../../../../components/RightPanel";
import { PageLoader } from "../../../../../../components/PageLoader/PageLoader";
import { useDispatchWrapper } from "../../../../../../hooks";
import { showMessage } from "../../../../../../redux/messages/messagesActions";
import { api } from "../../../../../../api/api";
import { uploadFile } from "../../../../../../components/FileUpload/utils/uploadFile";
import { sendGenericWhatsAppTemplateAsync } from "../../../../../../components/ShareViaWhatsApp";
import { useBillingCompanyContext } from "../../../../../../providers";
import { QuotationDocument } from "./QuotationDocument";
import { OutletQuotationDocument } from "./OutletQuotationDocument";
import { withS3Prefix } from "../../../../../../helpers/withS3Prefix";
import { useAppConfig } from "../../../../../../security/ApplicationProvider/ApplicationProvider";
import type { IEstimatePayload, TemplateVariant } from "./types";

interface IRoughEstimatePreviewV2Props {
  open: boolean;
  uuid: string;
  multiple_events: number;
  variant?: TemplateVariant;
  autoShare?: boolean;
  onClose: () => void;
}

export const RoughEstimatePreviewV2: React.FC<IRoughEstimatePreviewV2Props> = ({
  open,
  uuid,
  multiple_events,
  variant = "general",
  autoShare = false,
  onClose,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [sharing, setSharing] = React.useState(false);
  const [payloadData, setPayloadData] = React.useState<IEstimatePayload[] | null>(null);
  const [templateFile, setTemplateFile] = React.useState<string | null>(null);
  const [imageCache, setImageCache] = React.useState<Record<string, string>>({});
  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();
  const { logo: appLogo, companyName: appName } = useAppConfig();

  // Convert images to base64 via hidden <img> + canvas to bypass CORS for @react-pdf/renderer
  const imageToBase64 = (url: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } catch {
          resolve(""); // tainted canvas — CORS truly blocked
        }
      };
      img.onerror = () => resolve("");
      img.src = url;
    });
  };

  const prefetchImages = React.useCallback(async (data: IEstimatePayload[]) => {
    const keys = new Set<string>();
    // Add app logo to prefetch list
    if (appLogo) keys.add(appLogo);
    data.forEach((item) => {
      if (item.logo) keys.add(item.logo);
      if (item.gallery) {
        try {
          const gallery = typeof item.gallery === "string" ? JSON.parse(item.gallery || "[]") : item.gallery;
          if (Array.isArray(gallery)) gallery.forEach((img: string) => { if (img) keys.add(img); });
        } catch { }
      }
      if (item.payment_qr) {
        try {
          const qr = typeof item.payment_qr === "string" ? JSON.parse(item.payment_qr || "[]") : item.payment_qr;
          if (Array.isArray(qr) && qr[0]) {
            const qrKey = typeof qr[0] === "string" ? qr[0] : qr[0]?.path;
            if (qrKey) keys.add(qrKey);
          }
        } catch { }
      }
    });

    const cache: Record<string, string> = {};
    await Promise.all(
      Array.from(keys).map(async (key) => {
        const fullUrl = withS3Prefix(key);
        const base64 = await imageToBase64(fullUrl);
        if (base64) cache[fullUrl] = base64;
      })
    );
    return cache;
  }, [appLogo]);

  const fetchPayload = React.useCallback(async () => {
    if (!uuid) return;
    setLoading(true);
    try {
      const res = await api.get(
        `/leads/rough-estimate/${uuid}/PAYLOAD?multiple_events=${multiple_events}`,
      );
      const data: IEstimatePayload[] = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      const template = res.data?.templateFile || res.data?.template_file || res.data?.template || null;
      setTemplateFile(template);
      const cache = await prefetchImages(data);
      setImageCache(cache);
      setPayloadData(data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err?.response?.data?.message || "Failed to load preview",
          displayAs: "snackbar",
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [uuid, multiple_events, dispatch, prefetchImages]);

  React.useEffect(() => {
    if (open && uuid) {
      fetchPayload();
    }
  }, [open, uuid, fetchPayload]);

  const isZoyaQuotation = React.useMemo(() => {
    if (templateFile) {
      const normalized = templateFile.trim().toUpperCase();
      if (
        normalized === "ZOYA_QUOTATION" ||
        normalized === "ZOYA" ||
        normalized.includes("ZOYA")
      ) {
        return true;
      }
      if (
        normalized === "DEFAULT_QUOTATION" ||
        normalized === "DEFAULT" ||
        normalized.includes("DEFAULT")
      ) {
        return false;
      }
    }
    return variant === "outlet";
  }, [templateFile, variant]);

  const handleDownloadPdf = async () => {
    if (payloadData && payloadData.length > 0) {
      try {
        const doc = isZoyaQuotation ? (
          <OutletQuotationDocument
            data={payloadData}
            imageCache={imageCache}
            appLogo={appLogo}
            appName={appName}
          />
        ) : (
          <QuotationDocument
            data={payloadData}
            imageCache={imageCache}
            appLogo={appLogo}
            appName={appName}
          />
        );
        const blob = await pdf(doc).toBlob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Quotation-${payloadData[0]?.estimate_number || "preview"}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        dispatch(
          showMessage({
            type: "error",
            message: "Failed to generate PDF",
            displayAs: "snackbar",
          }),
        );
      }
    }
  };

  const handleShareV2 = async () => {
    if (!payloadData || payloadData.length === 0 || !uuid) return;
    setSharing(true);
    try {
      // 1. Generate PDF blob
      const doc = isZoyaQuotation ? (
        <OutletQuotationDocument
          data={payloadData}
          imageCache={imageCache}
          appLogo={appLogo}
          appName={appName}
        />
      ) : (
        <QuotationDocument
          data={payloadData}
          imageCache={imageCache}
          appLogo={appLogo}
          appName={appName}
        />
      );
      const blob = await pdf(doc).toBlob();

      // 2. Convert blob to File and upload to server
      const fileName = `Quotation-${payloadData[0]?.estimate_number || uuid}.pdf`;
      const pdfFile = new File([blob], fileName, { type: "application/pdf" });

      const uploadedPath = await uploadFile({
        file: pdfFile,
        module_name: "ROUGH_ESTIMATION",
        defaultReturn: "",
        asPayload: { title: uuid },
      });

      if (!uploadedPath) {
        throw new Error("Upload failed — no path returned");
      }

      // 3. Send WhatsApp via Generic Template API
      const companyOutletUuid =
        payloadData[0]?.company_outlet_uuid ||
        companyOutlet?.comapny?.company_outlet_uuid;
      const rawPhone = (payloadData[0]?.contact_number_one || "").replace(/\D/g, "");
      const countryCode = (payloadData[0]?.country_code || "91").replace(/\D/g, "") || "91";
      const toNumber = rawPhone.startsWith(countryCode) ? rawPhone : `${countryCode}${rawPhone}`;

      await sendGenericWhatsAppTemplateAsync({
        company_outlet_uuid: companyOutletUuid,
        platform: "META",
        env_key: "GENERIC_QUOTATION_WA",
        toNumber,
        payload: {
          name_of_guest: payloadData[0]?.name_of_guest || "",
          rough_estimation_pdf: withS3Prefix(uploadedPath),
        },
      });

      dispatch(
        showMessage({
          type: "success",
          message: "Quotation sent via WhatsApp successfully",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ||
            err?.message ||
            "Failed to share quotation",
          displayAs: "snackbar",
        }),
      );
    } finally {
      setSharing(false);
    }
  };

  // Auto-trigger share once payload + images are ready (when opened via Share V2 button)
  const hasAutoShared = React.useRef(false);
  React.useEffect(() => {
    if (autoShare && payloadData && payloadData.length > 0 && !loading && !hasAutoShared.current) {
      hasAutoShared.current = true;
      handleShareV2();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoShare, payloadData, loading]);

  return (
    <RightPanel
      heading="Quotation Preview"
      open={open}
      onClose={onClose}
      width="95%"
      actionButtons={() => (
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Button variant="contained" onClick={onClose}>
            <Close sx={{ fontSize: 18, mr: 0.5 }} />
            Close
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={handleDownloadPdf}
              disabled={!payloadData || payloadData.length === 0}
            >
              <Download sx={{ fontSize: 18, mr: 0.5 }} />
              Download
            </Button>
            <Button
              variant="contained"
              onClick={handleShareV2}
              disabled={!payloadData || payloadData.length === 0 || sharing}
              sx={{
                bgcolor: "#25D366",
                "&:hover": { bgcolor: "#1ebe5d" },
                "&:disabled": { bgcolor: "action.disabledBackground" },
              }}
            >
              {sharing ? (
                <CircularProgress size={16} sx={{ color: "#fff", mr: 0.5 }} />
              ) : (
                <WhatsAppIcon sx={{ fontSize: 18, mr: 0.5 }} />
              )}
              {sharing ? "Sending..." : "Send Whatsapp"}
            </Button>
          </Stack>
        </Stack>
      )}
    >
      <PageLoader loading={loading}>
        {payloadData && payloadData.length > 0 ? (
          <Box sx={{ width: "100%", height: "calc(100vh - 140px)" }}>
            <PDFViewer
              width="100%"
              height="100%"
              showToolbar={false}
              style={{ border: "none" }}
            >
              {isZoyaQuotation ? (
                <OutletQuotationDocument
                  data={payloadData}
                  imageCache={imageCache}
                  appLogo={appLogo}
                  appName={appName}
                />
              ) : (
                <QuotationDocument
                  data={payloadData}
                  imageCache={imageCache}
                  appLogo={appLogo}
                  appName={appName}
                />
              )}
            </PDFViewer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              textAlign: "center",
              p: 4,
            }}
          >
            <Typography variant="h6" color="textSecondary">
              No estimation data available.
            </Typography>
          </Box>
        )}
      </PageLoader>
    </RightPanel>
  );
};
